import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { isNil } from 'lodash'
import { useIdentity } from 'pages/Identity/Login'
import { Grid, Typography, Select, MenuItem, FormControl, InputLabel, Box, Alert, Snackbar, Menu, MenuItem as MenuItemComponent, useTheme, useMediaQuery, Stack, Pagination, CircularProgress } from '@mui/material'
import UploadIcon from '@mui/icons-material/Upload'
import { List } from 'components/Listing/List'
import Loader from 'components/Loader'
import { fetchCandidateJobs, fetchHospitalJobs, fetchPublicJobs } from 'src/redux/jobs'
import { fetchJobFilterValues } from 'src/redux/filters'
import JobContent from './JobContent'
import { useJobs } from './useJobs'
import JobDrawer from 'components/Drawers/JobDrawer/JobDrawer'
import SmartUploadDrawer from 'components/Drawers/SmartUploadDrawer/SmartUploadDrawer'
import { Filters } from '../../components/Filters'
import { FilterType } from '../../components/Filters/filterDefinitions'
import { candidateTypeDefinitions } from 'components/Filters/candidateTypeDefinitions'
import { filterDefinitions } from 'components/Filters/filterDefinitions'
import { fetchUpdateCandidateJob } from 'src/redux/candidateJob'
import { applyFilters } from 'components/Filters/helpers'

// AI Components
import MyStethiAIIcon from '/src/components/Icons/MyStethiAIIcon'
import AIJobCard from './AI/AIJobCard'
import AIJobCardExpanded from './AI/AIJobCardExpanded'
import JobDetailPanel from './AI/JobDetailPanel'
import AIChatBot from './AI/AIChatBot'
import ChatBottomBar from './AI/ChatBottomBar'
import { wrapTraditionalJobsAsRanked, mapJobListingArgumentsToFilterParams, mapFilterParamsToJobListingArguments, buildFilterChangeMessage } from './AI/filterSyncHelpers'
import { generateExampleQueries } from './AI/helpers'
import {
  updateAiJobFavorite,
  addChatMessage,
  sendChatMessage,
  addSystemMessage,
  setAiTyping,
  updateJobListingArguments,
  setActiveJobContext,
  setMobileDrawerState,
} from '../../redux/aiJobSearch'

const AI_JOBS_PER_PAGE = 10

const Jobs = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const signedIn = useSelector(state => state.identity.signedIn)

  const candidate = useSelector(state => state.candidate)
  const {
    candidateType,
    specialties = [],
    loading: candidateLoading,
  } = candidate
  const { loading: employerUserLoading } = useSelector(state => state.employerUser)
  const loading = candidateLoading || employerUserLoading
  const { employerId, employerUserId, candidateId, userId, isEmployerAdmin } = useIdentity()
  const [activeItem, setActiveItem] = useState()
  const [page, setPage] = useState(1)
  const pageSize = 50
  const { jobsList = [], isLoadingJobs } = useSelector(state => state.jobs)
  const { isLoadingFilters } = useSelector(state => state.filters).loading
  const filterValues = useSelector(state => state.filters.jobFilterValues) || {}
  const totalJobs = useSelector(state => state.jobs.totalJobs)
  const totalPages = Math.ceil(totalJobs / pageSize)
  const dataCall = employerUserId ? fetchHospitalJobs : signedIn ? fetchCandidateJobs : fetchPublicJobs
  const [searchParams, setSearchParams] = useSearchParams()
  const candidateTypeId = searchParams.get('candidateTypeId')
  const specialtyIdsParam = searchParams.get('specialtyIds')
  const [displayedData, setDisplayedData] = useState(jobsList)
  const [isApplyingFilters, setIsApplyingFilters] = useState(false)

  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [archivedFilter, setArchivedFilter] = useState('all')

  // AI-related state from Redux
  const activeSearchSource = useSelector(state => state.aiJobSearch.activeSearchSource)
  const isAIMode = activeSearchSource === 'ai'
  const { rankedJobs, jobListingArguments, resolvedFilters, conversationId, isAiTyping, loading: aiLoading } = useSelector(state => state.aiJobSearch)

  // Derived flags
  const isCandidate = signedIn && !employerUserId
  const showAiSidebar = isCandidate && !mobile

  // Floating chat widget state
  const [chatOpen, setChatOpen] = useState(true)
  const [selectedJob, setSelectedJob] = useState(null)
  const [aiPage, setAiPage] = useState(1)
  const [expandedJobId, setExpandedJobId] = useState(null)

  // AI results pulse animation state
  const [jobListPulseActive, setJobListPulseActive] = useState(false)
  const previousRankedJobsRef = useRef(rankedJobs)

  // Smart Job Upload notification state
  const [showUploadSuccess, setShowUploadSuccess] = useState(false)
  const [uploadedJobsCount, setUploadedJobsCount] = useState(0)

  // Upload menu state
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null)

  // Job drawer state (for manual upload)
  const { drawerJobOpen, setDrawerJobOpen } = useJobs()

  // Smart upload drawer state
  const [smartUploadDrawerOpen, setSmartUploadDrawerOpen] = useState(false)

  // Resizable panel state (for employer list/detail split)
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('jobsLeftPanelWidth')
    return saved ? parseInt(saved) : 420
  })
  const [isResizing, setIsResizing] = useState(false)

  // Resizable panel state (for candidate card list/detail split)
  const [candidateListWidth, setCandidateListWidth] = useState(() => {
    const saved = localStorage.getItem('jobsCandidateListWidth')
    return saved ? parseInt(saved) : 400
  })
  const [isCandidateResizing, setIsCandidateResizing] = useState(false)

  // Fallback labels for specialty IDs (used when filterValues hasn't loaded yet)
  const candidateSpecialtyLabels = useMemo(() => {
    const labels = {}
    specialties.forEach(s => { labels[String(s.id)] = s.name })
    return labels
  }, [specialties])

  // Compute display jobs: AI mode uses rankedJobs, traditional wraps jobsList
  const displayJobs = useMemo(() => {
    if (isCandidate && isAIMode) {
      return rankedJobs?.length > 0 ? rankedJobs : []
    }
    if (isCandidate) {
      return wrapTraditionalJobsAsRanked(displayedData)
    }
    return null // employer/not-signed-in use traditional List
  }, [isCandidate, isAIMode, rankedJobs, displayedData])

  // AI pagination
  const aiTotalPages = displayJobs ? Math.ceil(displayJobs.length / AI_JOBS_PER_PAGE) : 0
  const currentDisplayJobs = useMemo(
    () => displayJobs
      ? displayJobs.slice((aiPage - 1) * AI_JOBS_PER_PAGE, aiPage * AI_JOBS_PER_PAGE)
      : [],
    [displayJobs, aiPage],
  )

  // Pulse the job list panel when new AI results arrive
  useEffect(() => {
    if (
      rankedJobs !== previousRankedJobsRef.current &&
      rankedJobs.length > 0 &&
      isAIMode
    ) {
      setJobListPulseActive(true)
      const timer = setTimeout(() => setJobListPulseActive(false), 1500)
      previousRankedJobsRef.current = rankedJobs
      return () => clearTimeout(timer)
    }
    previousRankedJobsRef.current = rankedJobs
  }, [rankedJobs, isAIMode])

  // Listen for "view results" click from the chatbot to re-trigger pulse
  useEffect(() => {
    const handleViewResults = () => {
      setJobListPulseActive(false)
      requestAnimationFrame(() => setJobListPulseActive(true))
      const timer = setTimeout(() => setJobListPulseActive(false), 1500)
      return () => clearTimeout(timer)
    }
    window.addEventListener('ai-view-results', handleViewResults)
    return () => window.removeEventListener('ai-view-results', handleViewResults)
  }, [])

  // Reset AI page and expanded card when results change
  useEffect(() => {
    setAiPage(1)
    setExpandedJobId(null)
  }, [rankedJobs, displayedData])

  // Auto-select first job when results change and no valid selection exists
  useEffect(() => {
    if (!currentDisplayJobs.length) {
      if (!isAiTyping) {
        setSelectedJob(null)
      }
      return
    }
    const selectedStillInList = selectedJob && currentDisplayJobs.some(
      rj => (rj.job?.id || rj.jobId) === (selectedJob.job?.id || selectedJob.jobId)
    )
    if (!selectedStillInList) {
      setSelectedJob(currentDisplayJobs[0])
    }
  }, [currentDisplayJobs, isAiTyping, selectedJob])

  // When switching back to traditional mode (e.g. after reset), restore default filters
  const previousSearchSourceRef = useRef(activeSearchSource)
  useEffect(() => {
    if (previousSearchSourceRef.current === 'ai' && activeSearchSource === 'traditional') {
      // Restore default candidate filter params
      const newParams = new URLSearchParams()
      if (candidateType?.id) {
        newParams.set('candidateTypeId', candidateType.id)
        const candidateTypeDefinition = candidateTypeDefinitions.find(
          def => def.id === candidateType.id,
        )
        const specialtyFilterDefinition = filterDefinitions.find(
          filter => filter.name === 'specialty',
        )
        if (candidateTypeDefinition?.useCandidateSpecialty === true && specialties.length > 0) {
          newParams.set(specialtyFilterDefinition.queryStringKey, specialties[0].id)
        }
      }
      aiSyncGenerationRef.current += 1
      setSearchParams(newParams)
    }
    previousSearchSourceRef.current = activeSearchSource
  }, [activeSearchSource, candidateType, specialties, setSearchParams])

  // Bidirectional filter sync refs
  const aiSyncGenerationRef = useRef(0)
  const filterSyncGenerationRef = useRef(0)
  const previousParamsRef = useRef(new URLSearchParams(searchParams))
  const lastSentParamsRef = useRef(new URLSearchParams(searchParams))
  const filterSyncDebounceRef = useRef(null)

  // AI -> Filters sync: when AI returns jobListingArguments, update URL params
  // With resolved IDs from the backend, filterValues is no longer needed for mapping.
  useEffect(() => {
    if (activeSearchSource !== 'ai' || !jobListingArguments) return

    const aiParams = mapJobListingArgumentsToFilterParams(jobListingArguments, resolvedFilters, filterValues)
    const currentCandidateTypeId = searchParams.get('candidateTypeId')

    const newParams = new URLSearchParams(aiParams)
    if (currentCandidateTypeId && !newParams.has('candidateTypeId')) {
      newParams.set('candidateTypeId', currentCandidateTypeId)
    }

    aiSyncGenerationRef.current += 1
    previousParamsRef.current = new URLSearchParams(newParams)
    lastSentParamsRef.current = new URLSearchParams(newParams)
    setSearchParams(newParams)
  }, [jobListingArguments, activeSearchSource])

  // Filters -> AI sync: when user manually changes filters, update UI immediately and debounce API call
  useEffect(() => {
    if (aiSyncGenerationRef.current !== filterSyncGenerationRef.current) {
      filterSyncGenerationRef.current = aiSyncGenerationRef.current
      return
    }

    const message = buildFilterChangeMessage(previousParamsRef.current, searchParams, filterValues, candidateSpecialtyLabels)
    if (!message) return

    // Always show filter changes in the chat so the user sees them even before starting a conversation
    if (isCandidate) {
      dispatch(addSystemMessage(message))
    }
    previousParamsRef.current = new URLSearchParams(searchParams)

    // Only sync to AI backend if there's an active conversation
    if (!conversationId || activeSearchSource !== 'ai') {
      return
    }

    const updatedArgs = mapFilterParamsToJobListingArguments(searchParams, filterValues)

    // Preserve the chatbot's original location precision (city + radius) when
    // the user only changed non-location filters.
    const statesChanged = (previousParamsRef.current.get('states') || '') !== (searchParams.get('states') || '')
    if (!statesChanged && jobListingArguments?.locations) {
      updatedArgs.locations = jobListingArguments.locations
      updatedArgs.resolvedStateAbbreviations = jobListingArguments.resolvedStateAbbreviations
    }

    dispatch(updateJobListingArguments(updatedArgs))
    dispatch(setAiTyping(true))

    // Debounced: send to AI with accumulated changes since last sent message
    if (filterSyncDebounceRef.current) {
      clearTimeout(filterSyncDebounceRef.current)
    }
    const currentParams = new URLSearchParams(searchParams)
    filterSyncDebounceRef.current = setTimeout(() => {
      const accumulatedMessage = buildFilterChangeMessage(lastSentParamsRef.current, currentParams, filterValues, candidateSpecialtyLabels)
      if (accumulatedMessage) {
        lastSentParamsRef.current = currentParams
        dispatch(sendChatMessage({ message: `[Filter change] ${accumulatedMessage}`, conversationId }))
      }
    }, 400)

    return () => {
      if (filterSyncDebounceRef.current) {
        clearTimeout(filterSyncDebounceRef.current)
      }
    }
  }, [searchParams, conversationId, activeSearchSource, filterValues, isCandidate, dispatch])

  const exampleQueries = useMemo(
    () => generateExampleQueries(candidate),
    [candidate]
  )

  const startResize = (e) => {
    if (mobile) return
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const container = document.getElementById('jobs-container')
        if (!container) return

        const containerRect = container.getBoundingClientRect()
        const newWidth = e.clientX - containerRect.left

        if (newWidth >= 250 && newWidth <= 1000) {
          setLeftPanelWidth(newWidth)
        }
      })
    }

    const stopResize = () => {
      setIsResizing(false)
      localStorage.setItem('jobsLeftPanelWidth', leftPanelWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isResizing, leftPanelWidth, mobile])

  const startCandidateResize = (e) => {
    if (mobile) return
    setIsCandidateResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isCandidateResizing) return

    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const container = document.getElementById('candidate-jobs-container')
        if (!container) return

        const containerRect = container.getBoundingClientRect()
        const newWidth = e.clientX - containerRect.left

        if (newWidth >= 280 && newWidth <= 900) {
          setCandidateListWidth(newWidth)
        }
      })
    }

    const stopResize = () => {
      setIsCandidateResizing(false)
      localStorage.setItem('jobsCandidateListWidth', candidateListWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isCandidateResizing, candidateListWidth, mobile])

  const handleListItemClick = currId => {
    const activeJob = !isNil(currId)
      ? displayedData.find(j => `${j.id}` === `${currId}`)
      : displayedData.length > 0
        ? displayedData[0]
        : null
    setActiveItem(activeJob)
  }

  useEffect(() => {
    if (!signedIn && searchParams.size === 0) {
      dispatch(dataCall({ page, pageSize }))
    }
  }, [signedIn, dispatch, dataCall, page, pageSize])

  useEffect(() => {
    if (!mobile && jobsList.length > 0) {
      setActiveItem(jobsList[0])
    } else {
      setActiveItem(null)
    }
  }, [jobsList, mobile])

  useEffect(() => {
    if (location.state?.newlyUploaded) {
      setUploadedJobsCount(location.state.newlyUploaded)
      setShowUploadSuccess(true)
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  useEffect(() => {
    if (candidateType && candidateType.id && searchParams.size === 0) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('candidateTypeId', candidateType.id)
      const candidateTypeDefinition = candidateTypeDefinitions.find(
        def => def.id === candidateType.id,
      )
      const specialtyFilterDefinition = filterDefinitions.find(
        filter => filter.name === 'specialty',
      )
      if (candidateTypeDefinition?.useCandidateSpecialty === true) {
        if (specialties.length > 0) {
          newParams.set(specialtyFilterDefinition.queryStringKey, specialties[0].id)
        } else {
          newParams.delete(specialtyFilterDefinition.queryStringKey)
        }
      } else {
        newParams.delete(specialtyFilterDefinition.queryStringKey)
      }
      setSearchParams(newParams)
    }
  }, [candidateType, specialties])

  useEffect(() => {
    if (candidateTypeId) {
      dispatch(
        fetchJobFilterValues({
          employerId: employerId,
          candidateTypeId: candidateTypeId,
          specialtyIds: specialtyIdsParam ? specialtyIdsParam.split('|') : [],
        }),
      )
    }
    else {
      dispatch(
        fetchJobFilterValues({
          employerId: employerId,
        }),
      )
    }
  }, [candidateTypeId, employerId, specialtyIdsParam])

  useEffect(() => {
    setDisplayedData(jobsList)
  }, [jobsList])

  useEffect(() => {
    if ((searchParams.size > 0 || employerUserId || favoritesOnly || archivedFilter !== 'all') && !isAIMode) {
      const effectiveParams = new URLSearchParams(searchParams)
      if (favoritesOnly) effectiveParams.set('favorite', 'true')
      if (archivedFilter !== 'all') effectiveParams.set('archived', archivedFilter)

      setIsApplyingFilters(true)
      applyFilters({
        candidateId,
        employerUserId,
        searchParams: effectiveParams,
        dispatch,
        getData: dataCall,
        page,
        pageSize,
        employerId,
        isEmployerAdmin,
        userId,
      })
      const timer = setTimeout(() => {
        setIsApplyingFilters(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [searchParams, isAIMode, page, favoritesOnly, archivedFilter])

  const toggleFavoritesFilter = () => {
    setFavoritesOnly(!favoritesOnly)
  }

  const handleArchivedFilterChange = (event) => {
    setArchivedFilter(event.target.value)
  }

  const handleUploadMenuClick = (event) => {
    setUploadMenuAnchor(event.currentTarget)
  }

  const handleUploadMenuClose = () => {
    setUploadMenuAnchor(null)
  }

  const handleSmartUpload = () => {
    handleUploadMenuClose()
    setSmartUploadDrawerOpen(true)
  }

  const handleManualUpload = () => {
    handleUploadMenuClose()
    setDrawerJobOpen(true)
  }

  const leftPanelHeader = 'Results: ' + `${totalJobs ? totalJobs.toLocaleString() : 0}` + ' Jobs'

  const applyJobFilters = ({ searchParams }) => {
    if (!isAIMode) {
      const effectiveParams = new URLSearchParams(searchParams)
      if (favoritesOnly) effectiveParams.set('favorite', 'true')
      if (archivedFilter !== 'all') effectiveParams.set('archived', archivedFilter)

      applyFilters({
        candidateId,
        employerUserId,
        searchParams: effectiveParams,
        dispatch,
        getData: dataCall,
        page,
        pageSize,
        employerId,
        isEmployerAdmin,
        userId,
      })
    }
  }

  const onFavoriteClick = ({ favorite, jobId }) => {
    if (isAIMode) {
      dispatch(updateAiJobFavorite({ jobId, favorite }))
      if (selectedJob && (selectedJob.job?.id || selectedJob.jobId) === jobId) {
        setSelectedJob({
          ...selectedJob,
          job: {
            ...selectedJob.job,
            candidateJob: {
              ...selectedJob.job?.candidateJob,
              favorite,
            },
          },
        })
      }
    } else {
      const updatedData = displayedData.map(item =>
        item.id === jobId
          ? {
              ...item,
              candidateJob: {
                ...item.candidateJob,
                favorite,
              },
            }
          : item,
      )

      setDisplayedData(updatedData)

      if (activeItem && activeItem.id === jobId) {
        const updatedActiveItem = {
          ...activeItem,
          candidateJob: {
            ...activeItem.candidateJob,
            favorite,
          },
        }
        setActiveItem(updatedActiveItem)
      }
    }

    dispatch(fetchUpdateCandidateJob({ candidateId, jobId, favorite }))
  }

  // AI job card click -> mobile: navigate to detail page, desktop: open detail panel
  const handleJobClick = useCallback(rankedJob => {
    const jobId = rankedJob.job?.id || rankedJob.jobId
    if (mobile) {
      navigate(`/jobs/ai/${jobId}`, { state: { rankedJob, conversationId } })
    } else {
      setSelectedJob(rankedJob)
    }
  }, [mobile, navigate, conversationId])

  const handleDetailAskStethi = useCallback(() => {
    if (!selectedJob?.job) return
    const job = selectedJob.job
    dispatch(setActiveJobContext({
      id: job.id,
      title: job.title,
      employerName: job.employer?.name,
      city: job.city,
      state: job.state,
    }))
    if (mobile) {
      dispatch(setMobileDrawerState('expanded'))
    } else {
      setChatOpen(true)
    }
  }, [selectedJob, dispatch, mobile])

  // Browser back button closes detail panel on mobile
  useEffect(() => {
    const handlePopState = () => {
      setSelectedJob(null)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // "Ask Stethi" handler — toggles inline expanded card
  const handleAskStethi = useCallback((job) => {
    setExpandedJobId(prev => prev === job.id ? null : job.id)
  }, [])

  const handleExpandedViewFullDetails = useCallback((rankedJob) => {
    setSelectedJob(rankedJob)
  }, [])

  const handleExampleChipClick = useCallback((query) => {
    dispatch(addChatMessage({ type: 'user', message: query }))
    dispatch(sendChatMessage({ message: query, conversationId: undefined }))
  }, [dispatch])

  const handleChipDelete = useCallback((paramKey, value) => {
    const newParams = new URLSearchParams(searchParams)
    if (value === null) {
      newParams.delete(paramKey)
    } else {
      const current = newParams.get(paramKey)
      if (current) {
        const values = current.split('|').filter(v => v !== value)
        if (values.length > 0) {
          newParams.set(paramKey, values.join('|'))
        } else {
          newParams.delete(paramKey)
        }
      }
    }

    // Prevent the Filters->AI sync effect from double-processing this change
    aiSyncGenerationRef.current += 1
    previousParamsRef.current = new URLSearchParams(newParams)
    setSearchParams(newParams)

    // Update Redux immediately so the chip disappears
    const updatedArgs = mapFilterParamsToJobListingArguments(newParams, filterValues)

    // Preserve the chatbot's original location precision when deleting a
    // non-location chip (specialty, visa, etc.).
    if (paramKey !== 'states' && jobListingArguments?.locations) {
      updatedArgs.locations = jobListingArguments.locations
      updatedArgs.resolvedStateAbbreviations = jobListingArguments.resolvedStateAbbreviations
    }

    dispatch(updateJobListingArguments(updatedArgs))

    // Send the change to the AI directly
    if (conversationId) {
      const message = buildFilterChangeMessage(new URLSearchParams(searchParams), newParams, filterValues, candidateSpecialtyLabels)
      if (message) {
        dispatch(addSystemMessage(message))
        dispatch(setAiTyping(true))
        dispatch(sendChatMessage({ message: `[Filter change] ${message}`, conversationId }))
      }
    }
  }, [searchParams, setSearchParams, jobListingArguments, conversationId, filterValues, dispatch])


  // Candidate card-based job list
  const renderCandidateJobList = () => {
    const showLoading = (isLoadingJobs || loading || isApplyingFilters) && !isAIMode
    const showAiLoading = isAIMode && (aiLoading || isAiTyping) && currentDisplayJobs.length === 0

    if (showLoading || showAiLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 4 }}>
          <CircularProgress sx={{ color: isAIMode ? theme.palette.ai.main : theme.palette.primary.main }} />
        </Box>
      )
    }

    if (currentDisplayJobs.length > 0) {
      return (
        <>
          {/* Job Cards */}
          <Stack
            spacing={1.5}
            sx={{
              px: 2,
              py: 1,
              flex: 1,
              minWidth: 0,
              overflow: 'auto',
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[300],
                borderRadius: '2px',
              },
            }}
          >
            {currentDisplayJobs.map(rankedJob => {
              const jobId = rankedJob.job?.id || rankedJob.jobId
              if (expandedJobId === jobId) {
                return (
                  <AIJobCardExpanded
                    key={jobId}
                    rankedJob={rankedJob}
                    onClose={() => setExpandedJobId(null)}
                    onViewFullDetails={() => handleExpandedViewFullDetails(rankedJob)}
                    onFavoriteClick={onFavoriteClick}
                    isFavorite={!!rankedJob.job?.candidateJob?.favorite}
                  />
                )
              }
              return (
                <AIJobCard
                  key={jobId}
                  rankedJob={rankedJob}
                  isActive={selectedJob?.job?.id === rankedJob.job?.id}
                  onClick={() => handleJobClick(rankedJob)}
                  conversationId={conversationId}
                  onAskStethi={showAiSidebar ? () => handleAskStethi(rankedJob.job) : undefined}
                  onFavoriteClick={onFavoriteClick}
                  isFavorite={!!rankedJob.job?.candidateJob?.favorite}
                  isMobile={mobile}
                />
              )
            })}
          </Stack>

          {/* Pagination */}
          {isAIMode && aiTotalPages > 1 && (
            <Box sx={{ py: 0.75, pb: { xs: '72px', md: 0.75 }, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={aiTotalPages}
                page={aiPage}
                onChange={(e, v) => setAiPage(v)}
                color='primary'
                size='small'
              />
            </Box>
          )}
          {/* Traditional pagination for non-AI mode */}
          {!isAIMode && totalPages > 1 && (
            <Box sx={{ py: 0.75, pb: { xs: '72px', md: 0.75 }, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, v) => setPage(v)}
                color='primary'
                size='small'
              />
            </Box>
          )}
        </>
      )
    }

    // Empty state
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
        {(isLoadingJobs || loading) ? (
          <Loader />
        ) : (
          <Box sx={{ maxWidth: 360 }}>
            <Typography variant='h6' sx={{ color: theme.palette.grey[800], fontWeight: 600, mb: 0.5 }}>
              {isAIMode ? 'Ready to find your perfect job' : 'No jobs found'}
            </Typography>
            <Typography variant='body2' sx={{ color: theme.palette.grey[600], mb: 3 }}>
              {isAIMode
                ? 'Describe what you\'re looking for in the chat:'
                : 'Try adjusting your filters or search with Stethi AI.'
              }
            </Typography>
            {isAIMode && (
              <Stack spacing={1} sx={{ width: '100%' }}>
                {exampleQueries.map((query) => (
                  <Typography
                    key={query}
                    variant='body2'
                    onClick={() => handleExampleChipClick(query)}
                    sx={{
                      color: theme.palette.ai.dark,
                      cursor: 'pointer',
                      padding: '8px 12px',
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: theme.palette.ai.main,
                        color: theme.palette.ai.main,
                        backgroundColor: `${theme.palette.ai.light}20`,
                      },
                    }}
                  >
                    "{query}"
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Box>
    )
  }

  // Employer traditional list+detail layout
  const renderEmployerLayout = () => (
    <Box id="jobs-container" sx={{
      display: 'flex',
      flex: 1,
      minHeight: 0,
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
      px: { xs: 0, md: 4 },
    }}>
      <Grid
        container
        item
        xs={12}
        direction='column'
        alignContent='flex-start'
        sx={{
          height: '100%',
          minHeight: 0,
          flex: { xs: '1', md: `0 0 ${leftPanelWidth}px` },
          width: { xs: '100%', md: 'auto' },
          overflow: 'hidden',
          px: { xs: 0 },
          '& .MuiBox-root': {
            px: { xs: 0, md: 3 }
          }
        }}
      >
        <Grid
          container
          item
          alignItems='center'
          justifyContent='space-between'
          sx={{
            width: '100%',
            overflow: 'hidden',
            px: { xs: 2 },
            py: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: 'white',
            '& .MuiBox-root': {
              px: { xs: 0, md: 3 }
            }
          }}
        >
          <Grid container item xs={12} alignItems="center" justifyContent="space-between">
            <Grid item xs="auto">
              <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                {leftPanelHeader}
              </Typography>
            </Grid>
            <Grid container item xs="auto" spacing={2} justifyContent="flex-end" alignItems="center">
              {signedIn && employerUserId && (
                <Grid item>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                    <Select
                      value={archivedFilter}
                      onChange={handleArchivedFilterChange}
                      label="Status"
                      sx={{ fontSize: '0.875rem', height: '32px' }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All Jobs</MenuItem>
                      <MenuItem value="true" sx={{ fontSize: '0.875rem' }}>Archived</MenuItem>
                      <MenuItem value="false" sx={{ fontSize: '0.875rem' }}>Active</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid container alignItems='flex-start' sx={{
          maxHeight: '100%',
          overflow: 'auto',
          flex: 1,
          width: '100%'
        }}>
          <List
            setPage={setPage}
            page={page}
            data={displayedData}
            listType='job'
            handleListItemClick={handleListItemClick}
            dataTotal={totalJobs}
            totalPages={totalPages}
            signedIn={signedIn}
            setDisplayedData={setDisplayedData}
            onFavoriteClick={onFavoriteClick}
            setActiveItem={setActiveItem}
          />
        </Grid>
      </Grid>

      {/* Resizable Divider */}
      <Box
        onMouseDown={startResize}
        sx={{
          display: { xs: 'none', md: 'block' },
          flexShrink: 0,
          width: '4px',
          cursor: 'col-resize',
          backgroundColor: 'transparent',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '1px',
            height: '100%',
            backgroundColor: theme.palette.divider,
          },
          '&:hover, &:active': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      />

      {/* Right Panel */}
      <Grid
        container
        item
        xs={12}
        direction='column'
        sx={{
          height: '100%',
          minHeight: 0,
          flex: { xs: '1', md: '1' },
          pl: { md: 3 },
          overflow: 'auto',
          display: { xs: 'none', md: 'flex' },
        }}
      >
        <JobContent
          item={activeItem}
          signedIn={signedIn}
          employerUserId={employerUserId}
          onFavoriteClick={onFavoriteClick}
          jobs={jobsList}
          activeItem={activeItem}
          onUploadMenuClick={handleUploadMenuClick}
          uploadMenuAnchor={uploadMenuAnchor}
          onUploadMenuClose={handleUploadMenuClose}
          onSmartUpload={handleSmartUpload}
          onManualUpload={handleManualUpload}
        />
      </Grid>
    </Box>
  )

  return (
    <>
      <Box
        className="ai-smart-search-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          transition: 'margin-right 0.3s ease',
        }}
      >
        {/* Main content area */}
        {isCandidate ? (
          // Candidate: card-based layout with AI sidebar
          <Box
            id="candidate-jobs-container"
            data-ai-sidebar-parent
            sx={{
              display: 'flex',
              width: '100%',
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: 'hidden',
              ...(isAiTyping && {
                pointerEvents: 'none',
                opacity: 0.6,
              }),
            }}
          >
            {/* Job list panel */}
            <Box sx={{
              flex: { xs: '1', md: `0 0 ${candidateListWidth}px` },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
              ...(jobListPulseActive && {
                '@keyframes aiResultsPulse': {
                  '0%': { boxShadow: `inset 0 0 0 0 transparent` },
                  '40%': { boxShadow: `inset 0 0 20px 4px ${theme.palette.ai.light}4D` },
                  '100%': { boxShadow: `inset 0 0 0 0 transparent` },
                },
                animation: 'aiResultsPulse 1.5s ease-out',
              }),
            }}>
              <Box sx={{ flexShrink: 0 }}>
                <Filters
                  filterType={FilterType.JOB}
                  filterValues={filterValues}
                  listType='job'
                  loading={isLoadingFilters}
                  applyFilters={applyJobFilters}
                  disabled={isAiTyping}
                  resultCount={isAIMode ? displayJobs?.length : totalJobs}
                  favoritesOnly={favoritesOnly}
                  onToggleFavorites={signedIn && !employerUserId ? toggleFavoritesFilter : undefined}
                  isAIMode={isAIMode}
                  onAiChipDelete={handleChipDelete}
                />
              </Box>
              {renderCandidateJobList()}
            </Box>

            {/* Resizable Divider — desktop only */}
            {!mobile && (
              <Box
                onMouseDown={startCandidateResize}
                sx={{
                  display: { xs: 'none', md: 'block' },
                  flexShrink: 0,
                  width: '4px',
                  cursor: 'col-resize',
                  backgroundColor: 'transparent',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '1px',
                    height: '100%',
                    backgroundColor: theme.palette.divider,
                  },
                  '&:hover, &:active': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              />
            )}

            {/* Job Detail Panel — desktop only */}
            {!mobile && (
              selectedJob ? (
                <JobDetailPanel
                  rankedJob={selectedJob}
                  conversationId={conversationId}
                  onFavoriteClick={onFavoriteClick}
                  onAskStethi={handleDetailAskStethi}
                />
              ) : (
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>
                    {currentDisplayJobs.length === 0 ? 'No jobs to display' : 'Select a job to view details'}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    {currentDisplayJobs.length === 0
                      ? 'Try adjusting your filters or search criteria'
                      : 'Click on a job card to see the full details here'
                    }
                  </Typography>
                </Box>
              )
            )}

          </Box>
        ) : (
          <>
            <Box sx={{ flexShrink: 0 }}>
              <Filters
                filterType={FilterType.JOB}
                filterValues={filterValues}
                listType='job'
                loading={isLoadingFilters}
                applyFilters={applyJobFilters}
                disabled={isAiTyping}
                resultCount={isAIMode ? displayJobs?.length : totalJobs}
                favoritesOnly={favoritesOnly}
                onToggleFavorites={signedIn && !employerUserId ? toggleFavoritesFilter : undefined}
                isAIMode={isAIMode}
                onAiChipDelete={handleChipDelete}
              />
            </Box>
            {(isLoadingJobs || loading || isApplyingFilters) ? (
              <Loader />
            ) : (
              // Employer/public: traditional list+detail layout
              renderEmployerLayout()
            )}
          </>
        )}
      </Box>

      {/* Floating AI Chat Widget — desktop candidates only */}
      {isCandidate && !mobile && (
        <>
          {/* FAB button */}
          {!chatOpen && (
            <Box
              role="button"
              tabIndex={0}
              aria-label="open Stethi"
              onClick={() => setChatOpen(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setChatOpen(true) } }}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1300,
                display: 'flex',
                alignItems: 'center',
                background: `linear-gradient(135deg, ${theme.palette.ai.dark} 0%, ${theme.palette.ai.main} 100%)`,
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: theme.palette.ai.shadow,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: theme.palette.shadows?.heavy,
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.3rem', mr: 1 }} />
              <Typography
                variant="button"
                sx={{
                  color: theme.palette.common.white,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                }}
              >
                Stethi
              </Typography>
            </Box>
          )}

          {/* Floating chat panel — always mounted to preserve conversation state */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: 500,
              height: '75vh',
              maxHeight: 800,
              minHeight: 450,
              zIndex: 1300,
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              backgroundColor: theme.palette.background.default,
              visibility: chatOpen ? 'visible' : 'hidden',
              opacity: chatOpen ? 1 : 0,
              transform: chatOpen ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.25s ease, transform 0.25s ease, visibility 0.25s ease',
              display: 'flex',
              flexDirection: 'column',
              '& > div': {
                width: '100%',
                borderLeft: 'none !important',
              },
            }}
          >
            <AIChatBot inline onCollapse={() => setChatOpen(false)} />
          </Box>
        </>
      )}

      {/* Chat Bottom Bar — mobile only */}
      {isCandidate && mobile && <ChatBottomBar />}

      {/* Mobile: AIChatBot in drawer mode */}
      {isCandidate && mobile && <AIChatBot />}

      {/* Upload method selection menu */}
      <Menu
        anchorEl={uploadMenuAnchor}
        open={Boolean(uploadMenuAnchor)}
        onClose={handleUploadMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItemComponent onClick={handleManualUpload}>
          <UploadIcon sx={{ mr: 2 }} />
          Manual Upload
        </MenuItemComponent>
      </Menu>

      {/* Manual upload drawer */}
      <JobDrawer
        open={drawerJobOpen}
        onClose={() => setDrawerJobOpen(false)}
        isEdit={false}
        setOpenDrawer={setDrawerJobOpen}
      />

      {/* Smart upload drawer */}
      <SmartUploadDrawer
        open={smartUploadDrawerOpen}
        onClose={() => setSmartUploadDrawerOpen(false)}
      />

      {/* Success notification */}
      <Snackbar
        open={showUploadSuccess}
        autoHideDuration={6000}
        onClose={() => setShowUploadSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowUploadSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Successfully uploaded {uploadedJobsCount} job{uploadedJobsCount !== 1 ? 's' : ''}!
          Your new listings are now live.
        </Alert>
      </Snackbar>
    </>
  )
}

export default Jobs
